import { readonlyMapFilter, readonlyMapSet, readonlyMapRemove } from "../../utils/readonlyMap";
import type { RelayId } from "../../utils/relay";
import type { BoardUserSlice } from "../board_member/boardUserSlice";
import { emptyBoardUserSlice } from "../board_member/boardUserSlice";

const INIT = `INIT@${__filename}`;
const SET_MODAL_OPEN = `SET_MODAL_OPEN@${__filename}}`;
const SET_EDIT_MODE = `SET_EDIT_MODE@${__filename}}`;
const SET_USER_SLICE = `SET_USER_SLICE@${__filename}}`;
const SWITCH_USER_IN_SLICE = `SWITCH_USER_IN_SLICE@${__filename}`;
const REMOVE_FROM_EDITED_USER_MAP = `REMOVE_FROM_EDITED_USER_MAP@${__filename}`;

export type EditedUserMap = ReadonlyMap<RelayId, boolean>;

export interface User {
  id: RelayId;
  isMember: boolean;
}

export interface State {
  isModalOpen: boolean;
  isEditMode: boolean;
  userSlice: BoardUserSlice;
  editedUserMap: EditedUserMap;
}

const initialState: State = {
  isModalOpen: false,
  isEditMode: false,
  userSlice: emptyBoardUserSlice,
  editedUserMap: new Map(),
};

export function init() {
  return {
    type: INIT,
  };
}

export function setModalOpen(isOpen: boolean) {
  return {
    type: SET_MODAL_OPEN,
    isOpen,
  };
}

export function setEditMode(isEditMode: boolean) {
  return {
    type: SET_EDIT_MODE,
    isEditMode,
  };
}

export function setUserSlice(userSlice: BoardUserSlice) {
  return {
    type: SET_USER_SLICE,
    userSlice,
  };
}

function squashEditedUserMap(editedUserMap: EditedUserMap, userSlice: BoardUserSlice) {
  const originalUserMap = new Map<RelayId, User>();
  userSlice.data.forEach((user) => {
    originalUserMap.set(user.id, user);
  });
  return readonlyMapFilter(editedUserMap, (userIsMember, userId) => {
    // remove users that are not edited
    const originalUser = originalUserMap.get(userId!);
    return !originalUser || originalUser.isMember !== userIsMember;
  });
}

export function switchUserInSlice(user: User, editedUserMap: EditedUserMap, userSlice: BoardUserSlice) {
  const newEditedUserMap = readonlyMapSet(editedUserMap, user.id, !user.isMember);

  return {
    type: SWITCH_USER_IN_SLICE,
    editedUserMap: squashEditedUserMap(newEditedUserMap, userSlice),
  };
}

export function removeFromEditedUserMap(userId: RelayId) {
  return {
    type: REMOVE_FROM_EDITED_USER_MAP,
    userId,
  };
}

export function reducer(state = initialState, action): State {
  switch (action.type) {
    case INIT:
      return { ...initialState, isModalOpen: state.isModalOpen };
    case SET_MODAL_OPEN:
      return {
        ...state,
        isModalOpen: action.isOpen,
      };
    case SET_EDIT_MODE:
      return {
        ...state,
        isEditMode: action.isEditMode,
      };
    case SET_USER_SLICE:
      return {
        ...state,
        userSlice: action.userSlice,
      };
    case SWITCH_USER_IN_SLICE:
      return {
        ...state,
        editedUserMap: action.editedUserMap,
      };
    case REMOVE_FROM_EDITED_USER_MAP:
      return {
        ...state,
        editedUserMap: readonlyMapRemove(state.editedUserMap, action.userId),
      };
    default:
      return state;
  }
}
